import React, { useState, useRef, useEffect } from 'react';
import '../../assets/styles/components.css';
import './Services.css';
import RequestQuoteModal from '../Common/Modals/RequestQuoteModal';

const Services = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  const services = [
    {
      id: 1,
      title: "WordPress Plugin Development",
      description: "Custom plugins tailored to your website's unique needs—whether you're adding new WooCommerce functionality, building custom dashboards, or automating workflows.",
      icon: "fab fa-wordpress",
      gradient: "linear-gradient(135deg, #00c6ff, #0072ff)"
    },
    {
      id: 2,
      title: "Shopify App Integration",
      description: "Extend your Shopify store with private or public apps that handle payment logic, storefront customization, and backend automation.",
      icon: "fab fa-shopify",
      gradient: "linear-gradient(135deg, #7db9e8, #1e5799)"
    },
    {
      id: 3,
      title: "Low-Code Platform Extensions",
      description: "Need a custom widget or tool for Webflow, Wix, or Bubble? We build extendable components that play well with your platform.",
      icon: "fas fa-code",
      gradient: "linear-gradient(135deg, #ff7e5f, #feb47b)"
    },
    {
      id: 4,
      title: "Web & App Development",
      description: "Custom web applications and mobile apps built with modern technologies. From responsive websites to progressive web apps and native mobile applications.",
      icon: "fas fa-laptop-code",
      gradient: "linear-gradient(135deg, #6a11cb, #2575fc)"
    }
  ];

  const handleOpenModal = (service) => {
    setActiveService(service);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveService(null);
  };

  // Update active index on scroll
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollLeft = scrollContainer.scrollLeft;
      const cardWidth = scrollContainer.offsetWidth * 0.8; // 80% of container width
      const gap = 20; // gap between cards
      const totalCardWidth = cardWidth + gap;
      
      const newIndex = Math.round(scrollLeft / totalCardWidth);
      setActiveIndex(Math.max(0, Math.min(newIndex, services.length - 1)));
    };

    // Set initial active index
    handleScroll();

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [services.length]);

  // Scroll to specific card
  const scrollToCard = (index) => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const cardWidth = scrollContainer.offsetWidth * 0.8;
      const gap = 20;
      const totalCardWidth = cardWidth + gap;
      
      scrollContainer.scrollTo({
        left: index * totalCardWidth,
        behavior: 'smooth'
      });
      setActiveIndex(index);
    }
  };

  // Handle card click for mobile
  const handleCardClick = (index) => {
    scrollToCard(index);
  };

  return (
    <section className="services-section" id="services">
      <div className="services-header">
        <h2 className="section-title">Our Services</h2>
        <div className="title-underline"></div>
        <p className="section-description">
          At Genesis Sequence Solutions, we specialize in powerful, scalable solutions for 
          low-code platforms that empower businesses to go further, faster.
        </p>
      </div>

      <div className="services-grid" ref={scrollContainerRef}>
        {services.map((service, index) => (
          <div 
            className={`service-card ${index === activeIndex ? 'active' : ''}`}
            key={service.id}
            onClick={() => handleCardClick(index)}
          >
            <div 
              className="service-icon" 
              style={{ background: service.gradient }}
            >
              <i className={service.icon}></i>
            </div>
            <h3>{service.title}</h3>
            <p>{service.description}</p>
            <button 
              className="request-quote-btn" 
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal(service);
              }}
            >
              Request a Quote
            </button>
          </div>
        ))}
      </div>

      {/* Carousel Indicators */}
      <div className="carousel-indicators">
        {services.map((_, index) => (
          <div
            key={index}
            className={`indicator ${index === activeIndex ? 'active' : ''}`}
            onClick={() => scrollToCard(index)}
          />
        ))}
      </div>

      <RequestQuoteModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        service={activeService}
      />
    </section>
  );
};

export default Services;